import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  FormControlLabel,
  Switch,
  TextField
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1)
    }
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120
  }
}));

const PartnerSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(100, "Too Long!")
    .required("Nome é obrigatório"),
  phone: Yup.string().required("Telefone é obrigatório"),
  email: Yup.string().email("E-mail é inválido").required("E-mail é obrigatório"),
  document: Yup.string().required("Documento é obrigatório"),
  commission: Yup.number().required("Comissão é obrigatória"),
  typeCommission: Yup.string().required("Tipo de comissão é obrigatório"),
  referralCode: Yup.string().nullable(),
  link: Yup.string().url("Informe uma URL válida").nullable()
});

const initialState = {
  name: "",
  phone: "",
  email: "",
  document: "",
  commission: "",
  typeCommission: "percentual",
  walletId: "",
  referralCode: "",
  link: "",
  isDefault: false
};

const PartnerModal = ({ open, onClose, partnerId }) => {
  const classes = useStyles();

  const [partner, setPartner] = useState(initialState);

  useEffect(() => {
    const fetchPartner = async () => {
      if (!partnerId) return;
      try {
        const { data } = await api.get(`/partners/${partnerId}`);
        setPartner(prevState => ({ ...prevState, ...data }));
      } catch (err) {
        toastError(err);
      }
    };

    fetchPartner();
  }, [partnerId, open]);

  const handleClose = () => {
    onClose();
    setPartner(initialState);
  };

  const handleSavePartner = async values => {
    const partnerData = { ...values };
    try {
      if (partnerId) {
        await api.put(`/partners/${partnerId}`, partnerData);
      } else {
        await api.post("/partners", partnerData);
      }
      toast.success(i18n.t("partnerModal.success"));
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth scroll="paper">
        <DialogTitle id="form-dialog-title">
          {partnerId
            ? `${i18n.t("partnerModal.title.edit")}`
            : `${i18n.t("partnerModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={partner}
          enableReinitialize={true}
          validationSchema={PartnerSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSavePartner(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("partnerModal.form.name")}
                    autoFocus
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </div>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("partnerModal.form.phone")}
                    name="phone"
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  <Field
                    as={TextField}
                    label={i18n.t("partnerModal.form.email")}
                    name="email"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </div>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("partnerModal.form.document")}
                    name="document"
                    error={touched.document && Boolean(errors.document)}
                    helperText={touched.document && errors.document}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  <Field
                    as={TextField}
                    label={i18n.t("partnerModal.form.walletId")}
                    name="walletId"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </div>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("partnerModal.form.commission")}
                    name="commission"
                    type="number"
                    error={touched.commission && Boolean(errors.commission)}
                    helperText={touched.commission && errors.commission}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    error={touched.typeCommission && Boolean(errors.typeCommission)}
                  >
                    <InputLabel id="type-commission-label">
                      {i18n.t("partnerModal.form.typeCommission")}
                    </InputLabel>
                    <Field
                      as={Select}
                      labelId="type-commission-label"
                      label={i18n.t("partnerModal.form.typeCommission")}
                      name="typeCommission"
                    >
                      <MenuItem value="percentual">
                        {i18n.t("partnerModal.form.typeCommissionPercentage")}
                      </MenuItem>
                      <MenuItem value="fixo">
                        {i18n.t("partnerModal.form.typeCommissionFixed")}
                      </MenuItem>
                    </Field>
                  </FormControl>
                </div>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("partnerModal.form.referralCode")}
                    name="referralCode"
                    placeholder="001"
                    helperText={i18n.t("partnerModal.form.referralCodeHelper")}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </div>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("partnerModal.form.link")}
                    name="link"
                    error={touched.link && Boolean(errors.link)}
                    helperText={touched.link && errors.link}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                </div>
                <div className={classes.multFieldLine}>
                  <FormControlLabel
                    control={
                      <Field
                        as={Switch}
                        color="primary"
                        name="isDefault"
                        checked={values.isDefault}
                      />
                    }
                    label={i18n.t("partnerModal.form.isDefault")}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("partnerModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {partnerId
                    ? `${i18n.t("partnerModal.buttons.okEdit")}`
                    : `${i18n.t("partnerModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default PartnerModal;
